/**
 * DevSidebar Component
 *
 * A permanent dev sidebar (feature-gated) that shows:
 * - Raw input (CollectedData) live JSON
 * - Normalized input (SystemRecInput) live JSON
 * - Recent changes (last 50 field updates)
 * - Mapping warnings (enum mismatches, dropped fields, etc.)
 *
 * Feature gated:
 * - Enabled ONLY by runtime flag: URL ?dev=1 OR localStorage.devtools === '1'
 * - Works on deployed builds (not build-time gated)
 * - Persists across sessions via localStorage
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getDiagnosticsState,
  subscribe,
  clearWarnings,
  clearDiagnostics,
  setInstrumentationEnabled,
  questionMappings,
  type MappingDiagnosticsState,
  type MappingChangeEvent,
  type MappingWarning,
  type QuestionMapping,
} from '../mapping';
import { ATLAS_DATA_CONTRACT_VERSION, ENGINE_RULESET_VERSION, UI_MAPPING_VERSION } from '../contracts/dataContract';
import { engineCapabilities, type EngineCapabilities } from './engineCapabilities';
import { fields as contractFields, type FieldDef } from '../contract/atlasDataContract';

// =============================================================================
// GLOBAL SIDEBAR STATE
// =============================================================================

type SidebarStateListener = (isOpen: boolean) => void;
const sidebarListeners: Set<SidebarStateListener> = new Set();
let globalSidebarOpen = false;

// Track if a modal is open to auto-collapse DevSidebar
let modalIsOpen = false;
let wasOpenBeforeModal = false;

// DevOverlay state management
type OverlayStateListener = (isOpen: boolean) => void;
const overlayListeners: Set<OverlayStateListener> = new Set();
let globalOverlayOpen = false;
let wasOverlayOpenBeforeModal = false;

/**
 * Open the DevSidebar from anywhere in the app.
 * This also enables instrumentation if not already enabled.
 */
export function openDevSidebar(): void {
  if (!shouldShowDevtools()) {
    return;
  }
  setInstrumentationEnabled(true);
  globalSidebarOpen = true;
  sidebarListeners.forEach(listener => listener(true));
}

/**
 * Close the DevSidebar
 */
export function closeDevSidebar(): void {
  globalSidebarOpen = false;
  sidebarListeners.forEach(listener => listener(false));
}

/**
 * Toggle the DevSidebar
 */
export function toggleDevSidebar(): void {
  if (!shouldShowDevtools()) {
    return;
  }
  if (globalSidebarOpen) {
    closeDevSidebar();
  } else {
    openDevSidebar();
  }
}

/**
 * Check if sidebar is currently open
 */
export function isSidebarOpen(): boolean {
  return globalSidebarOpen;
}

/**
 * Subscribe to DevOverlay state changes
 */
export function subscribeToOverlayState(listener: OverlayStateListener): () => void {
  overlayListeners.add(listener);
  return () => overlayListeners.delete(listener);
}

/**
 * Notify that DevOverlay state changed
 */
export function notifyOverlayStateChange(isOpen: boolean): void {
  globalOverlayOpen = isOpen;
}

/**
 * Notify DevSidebar that a modal has opened.
 * This will auto-collapse the sidebar to prevent overlap.
 */
export function notifyModalOpened(): void {
  if (!shouldShowDevtools()) {
    return;
  }
  modalIsOpen = true;
  wasOpenBeforeModal = globalSidebarOpen;
  wasOverlayOpenBeforeModal = globalOverlayOpen;
  
  // Always close the sidebar when modal opens, regardless of how it was opened
  globalSidebarOpen = false;
  sidebarListeners.forEach(listener => listener(false));
  
  // Also notify overlay listeners to collapse
  globalOverlayOpen = false;
  overlayListeners.forEach(listener => listener(false));
}

/**
 * Notify DevSidebar that a modal has closed.
 * This will restore the sidebar state if it was open before.
 */
export function notifyModalClosed(): void {
  if (!shouldShowDevtools()) {
    return;
  }
  modalIsOpen = false;
  
  // Optionally restore previous sidebar state
  if (wasOpenBeforeModal) {
    openDevSidebar();
  }
  wasOpenBeforeModal = false;
  
  // Optionally restore previous overlay state
  if (wasOverlayOpenBeforeModal) {
    globalOverlayOpen = true;
    overlayListeners.forEach(listener => listener(true));
  }
  wasOverlayOpenBeforeModal = false;
}

// =============================================================================
// TYPES
// =============================================================================

type TabId = 'raw' | 'normalized' | 'changes' | 'warnings' | 'tools';

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Copy text to clipboard
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Format JSON with syntax highlighting
 */
function formatJson(obj: unknown): string {
  return JSON.stringify(obj, null, 2);
}

/**
 * Format timestamp
 */
function formatTime(ts: number): string {
  const date = new Date(ts);
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  return `${time}.${ms}`;
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface TabButtonProps {
  id: TabId;
  active: boolean;
  onClick: (id: TabId) => void;
  label: string;
  badge?: number;
}

function TabButton({ id, active, onClick, label, badge }: TabButtonProps) {
  return (
    <button
      onClick={() => onClick(id)}
      style={{
        padding: '8px 12px',
        border: 'none',
        background: active ? '#2d3748' : 'transparent',
        color: active ? '#fff' : '#a0aec0',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: active ? 600 : 400,
        borderRadius: '4px 4px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}
    >
      {label}
      {badge !== undefined && badge > 0 && (
        <span
          style={{
            background: id === 'warnings' ? '#e53e3e' : '#4a5568',
            color: '#fff',
            fontSize: '10px',
            padding: '2px 6px',
            borderRadius: '10px',
            minWidth: '18px',
            textAlign: 'center',
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

interface CopyButtonProps {
  onClick: () => void;
  copied: boolean;
}

function CopyButton({ onClick, copied }: CopyButtonProps) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 8px',
        border: '1px solid #4a5568',
        background: copied ? '#38a169' : 'transparent',
        color: copied ? '#fff' : '#a0aec0',
        cursor: 'pointer',
        fontSize: '11px',
        borderRadius: '4px',
      }}
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

interface JsonViewProps {
  data: unknown;
  onCopy: () => void;
  copied: boolean;
}

function JsonView({ data, onCopy, copied }: JsonViewProps) {
  const isEmpty = !data || (typeof data === 'object' && Object.keys(data).length === 0);
  
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '8px', borderBottom: '1px solid #2d3748', display: 'flex', justifyContent: 'flex-end' }}>
        <CopyButton onClick={onCopy} copied={copied} />
      </div>
      {isEmpty ? (
        <div style={{ 
          flex: 1,
          padding: '32px 24px',
          color: '#a0aec0',
          textAlign: 'center',
          fontSize: '12px',
          lineHeight: '1.6',
        }}>
          <div style={{ marginBottom: '16px', fontSize: '32px' }}>📭</div>
          <div style={{ fontWeight: 600, marginBottom: '8px', color: '#e2e8f0' }}>
            Mapping is empty
          </div>
          <div style={{ marginBottom: '16px' }}>
            This could mean:
          </div>
          <ul style={{ 
            textAlign: 'left',
            listStyle: 'none',
            padding: 0,
            margin: '0 auto',
            maxWidth: '280px',
          }}>
            <li style={{ marginBottom: '8px' }}>• No questions answered yet</li>
            <li style={{ marginBottom: '8px' }}>• Data cleared/reset after hydration</li>
            <li style={{ marginBottom: '8px' }}>• Wrong store being watched</li>
            <li style={{ marginBottom: '8px' }}>• Dev flag not parsed correctly</li>
          </ul>
          <div style={{ 
            marginTop: '20px',
            padding: '12px',
            background: '#2d3748',
            borderRadius: '6px',
            fontSize: '11px',
            color: '#cbd5e0',
          }}>
            💡 <strong>Tip:</strong> Check the Dev Overlay (🔧 DEV button) for event trace showing when mapping becomes empty.
          </div>
        </div>
      ) : (
        <pre
          style={{
            flex: 1,
            margin: 0,
            padding: '12px',
            overflow: 'auto',
            fontSize: '11px',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            lineHeight: 1.5,
            color: '#e2e8f0',
          }}
        >
          {formatJson(data)}
        </pre>
      )}
    </div>
  );
}

interface ChangeItemProps {
  change: MappingChangeEvent;
}

function ChangeItem({ change }: ChangeItemProps) {
  const layerColor = change.layer === 'raw' ? '#4299e1' : '#48bb78';

  return (
    <div
      style={{
        padding: '8px 12px',
        borderBottom: '1px solid #2d3748',
        fontSize: '11px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ color: '#a0aec0' }}>{formatTime(change.ts)}</span>
        <span
          style={{
            background: layerColor,
            color: '#fff',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            textTransform: 'uppercase',
          }}
        >
          {change.layer}
        </span>
      </div>
      <div style={{ color: '#f7fafc', fontWeight: 500 }}>
        {change.questionId}.{change.path}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '4px', color: '#a0aec0' }}>
        <span style={{ color: '#fc8181' }}>{JSON.stringify(change.prev)}</span>
        <span>&rarr;</span>
        <span style={{ color: '#68d391' }}>{JSON.stringify(change.next)}</span>
      </div>
    </div>
  );
}

interface WarningItemProps {
  warning: MappingWarning;
}

function WarningItem({ warning }: WarningItemProps) {
  const typeColors: Record<string, string> = {
    enum_mismatch: '#e53e3e',
    dropped_field: '#ed8936',
    unmapped_question: '#d69e2e',
    unexpected_raw_path: '#9f7aea',
    missing_required: '#e53e3e',
    path_not_found: '#fc8181',
  };

  return (
    <div
      style={{
        padding: '8px 12px',
        borderBottom: '1px solid #2d3748',
        borderLeft: `3px solid ${typeColors[warning.type] || '#a0aec0'}`,
        fontSize: '11px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span
          style={{
            color: typeColors[warning.type] || '#a0aec0',
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '10px',
          }}
        >
          {warning.type.replace(/_/g, ' ')}
        </span>
        <span style={{ color: '#718096', fontSize: '10px' }}>{formatTime(warning.ts)}</span>
      </div>
      <div style={{ color: '#f7fafc' }}>{warning.message}</div>
      {warning.details && (
        <pre
          style={{
            margin: '4px 0 0',
            padding: '4px 8px',
            background: '#1a202c',
            borderRadius: '4px',
            fontSize: '10px',
            color: '#a0aec0',
            overflow: 'auto',
          }}
        >
          {JSON.stringify(warning.details, null, 2)}
        </pre>
      )}
    </div>
  );
}

// =============================================================================
// DEVTOOLS CONTRACT HELPERS (exported for testing)
// =============================================================================

/**
 * Build the dev-enable URL from any base href.
 * Uses URLSearchParams so existing query params are preserved.
 */
export function buildDevUrl(href?: string): string {
  const base = href ?? (typeof window !== 'undefined' ? window.location.href : 'http://localhost/');
  const url = new URL(base);
  url.searchParams.set('dev', '1');
  return url.toString();
}

/**
 * Format a one-liner versions summary suitable for copy-to-clipboard.
 */
export function formatVersionsSummary(): string {
  return `dataContract=v${ATLAS_DATA_CONTRACT_VERSION} uiMapping=v${UI_MAPPING_VERSION} engineRuleset=v${ENGINE_RULESET_VERSION}`;
}

/** Default file path for all UI question mapping sections. */
const MAPPING_REGISTRY_PATH = 'apps/standalone-web/src/mapping/registry.ts';

/**
 * Source section → relative file path mapping used in DevTools "copy path" links.
 * Sections not listed here fall back to MAPPING_REGISTRY_PATH.
 */
export const SOURCE_FILE_PATHS: Record<string, string> = {
  engine: 'apps/standalone-web/src/devtools/engineCapabilities.ts',
};

/**
 * Compute human-readable mismatch reasons comparing a UI question mapping
 * against the engine capabilities registry.
 *
 * When a FieldDef from atlasDataContract is provided, enum mismatches that are
 * already handled by `engineEnumMap` are annotated with a "adapter resolves"
 * note rather than treated as bugs, and engineKey differences are surfaced.
 */
export function computeMismatchReasons(
  mapping: Pick<QuestionMapping, 'questionId' | 'normalizedPaths' | 'required' | 'enums'>,
  engCapabilities: Pick<EngineCapabilities, 'supportedInputs' | 'supportedEnums'>,
  fieldDef?: FieldDef,
): string[] {
  const reasons: string[] = [];
  const engineFieldMap = new Map(engCapabilities.supportedInputs.map((input) => [input.key, input]));

  // Resolve the engine entry: try questionId → normalizedPaths → engineKey from FieldDef
  const direct = engineFieldMap.get(mapping.questionId);
  const viaPath = mapping.normalizedPaths.find(p => engineFieldMap.has(p));
  const viaEngineKey = fieldDef?.engineKey ? engineFieldMap.get(fieldDef.engineKey) : undefined;
  const engine = direct ?? (viaPath ? engineFieldMap.get(viaPath) : undefined) ?? viaEngineKey;

  // Surface engineKey difference so the Contract tab can link the two sides.
  if (!direct && !viaPath && viaEngineKey && fieldDef?.engineKey) {
    reasons.push(`Canonical key '${mapping.questionId}' maps to engine key '${fieldDef.engineKey}' via atlasDataContract adapter`);
  }

  // Enum mismatch: prefer enumRef-based lookup for precision, fall back to heuristic overlap.
  // Skip when the field has no enums (numeric/boolean/array/object fields).
  const uiEnumValues = Object.values(mapping.enums ?? {}).flat();
  if (uiEnumValues.length > 0 && engine) {
    const enumRef = engine.enumRef;
    let enumEntry: [string, string[]] | undefined;
    if (enumRef) {
      enumEntry = [enumRef, engCapabilities.supportedEnums[enumRef] ?? []];
    } else {
      // Heuristic: find any engine enum that shares at least one value with the UI enum,
      // but only when the overlap is not explained by a common 'unknown' sentinel.
      const meaningfulUiValues = uiEnumValues.filter(v => v !== 'unknown');
      enumEntry = Object.entries(engCapabilities.supportedEnums).find(([, values]) =>
        values.some(v => meaningfulUiValues.includes(v))
      );
    }

    if (enumEntry) {
      const [enumKey, engineValues] = enumEntry;
      const uiOnly = uiEnumValues.filter(v => !engineValues.includes(v));
      const engineOnly = engineValues.filter(v => !uiEnumValues.includes(v));

      if (uiOnly.length > 0) {
        const adapterCovers = fieldDef?.engineEnumMap
          ? uiOnly.every(v => v in (fieldDef.engineEnumMap ?? {}))
          : false;
        const suffix = adapterCovers
          ? ' — engineEnumMap in atlasDataContract maps these; adapter resolves'
          : '';
        reasons.push(`UI has enum values not in engine (${enumKey}): ${uiOnly.join(', ')}${suffix}`);
      }

      if (engineOnly.length > 0) {
        reasons.push(`Engine has enum values not in UI (${enumKey}): ${engineOnly.join(', ')}`);
      }
    }
  }

  // Required mismatch
  if (engine?.required && !mapping.required) {
    reasons.push('Engine requires this field but UI marks it optional');
  }

  return reasons;
}

// NOTE: ContractExplorer has been moved to DevOverlay.tsx (white overlay).
// DevSidebar retains raw/normalized data, changes, warnings, and tools.

// =============================================================================
// CONTRACT EXPLORER — moved to DevOverlay.tsx (white overlay)
// =============================================================================

// (ContractExplorer removed — moved to DevOverlay.tsx)

// Suppress unused-import warnings for helpers still used by external consumers
void computeMismatchReasons;
void formatVersionsSummary;
void SOURCE_FILE_PATHS;

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function DevSidebar() {
  const [state, setState] = useState<MappingDiagnosticsState>(getDiagnosticsState);
  const [activeTab, setActiveTab] = useState<TabId>('changes');
  const [copied, setCopied] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isOpenViaButton, setIsOpenViaButton] = useState(globalSidebarOpen);
  const [docIdInput, setDocIdInput] = useState('');

  // Subscribe to diagnostics state changes
  useEffect(() => {
    const unsubscribe = subscribe(setState);
    return unsubscribe;
  }, []);

  // Subscribe to global sidebar open/close state
  useEffect(() => {
    const listener = (isOpen: boolean) => {
      setIsOpenViaButton(isOpen);
      if (isOpen) {
        setIsCollapsed(false); // Expand when opened via button
      } else {
        setIsCollapsed(true); // Collapse when closed
      }
    };
    sidebarListeners.add(listener);
    return () => {
      sidebarListeners.delete(listener);
    };
  }, []);

  // Handle copy
  const handleCopy = useCallback(async () => {
    let content = '';
    switch (activeTab) {
      case 'raw':
        content = formatJson(state.rawInput);
        break;
      case 'normalized':
        content = formatJson(state.normalizedInput);
        break;
      case 'changes':
        content = formatJson(state.lastChanges);
        break;
      case 'warnings':
        content = formatJson(state.warnings);
        break;
    }

    const success = await copyToClipboard(content);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [activeTab, state]);

  // Handle clear
  const handleClear = useCallback(() => {
    if (activeTab === 'warnings') {
      clearWarnings();
    } else if (activeTab === 'changes') {
      clearDiagnostics();
    }
  }, [activeTab]);

  // Handle close button
  const handleClose = useCallback(() => {
    if (isOpenViaButton) {
      closeDevSidebar();
    } else {
      setIsCollapsed(true);
    }
  }, [isOpenViaButton]);

  // Handle open doc
  const handleOpenDoc = useCallback(() => {
    if (!docIdInput.trim()) return;
    
    // Navigate to interactive page with docId query param
    const trimmedDocId = docIdInput.trim();
    window.location.href = `/story/interactive?docId=${encodeURIComponent(trimmedDocId)}`;
  }, [docIdInput]);

  // Don't render if not enabled AND not opened via button
  if (!state.enabled && !isOpenViaButton) {
    return null;
  }

  if (isCollapsed && !isOpenViaButton) {
    return (
      <button
        onClick={() => setIsCollapsed(false)}
        style={{
          position: 'fixed',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          background: '#d97706',
          color: '#fff',
          border: '1px solid #b45309',
          borderRight: 'none',
          padding: '12px 8px',
          cursor: 'pointer',
          zIndex: 9999,
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          fontSize: '12px',
          fontWeight: 700,
          borderRadius: '4px 0 0 4px',
        }}
      >
        DEVTOOLS {state.warnings.length > 0 && `(${state.warnings.length})`}
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        bottom: 0,
        width: '400px',
        background: '#1a202c',
        borderLeft: '1px solid #2d3748',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 9999,
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #2d3748',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ color: '#f7fafc', fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          Mapping DevTools
          <span style={{
            background: '#d97706',
            color: '#fff',
            padding: '2px 6px',
            fontSize: '10px',
            fontWeight: 700,
            borderRadius: '4px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            DEVTOOLS
          </span>
        </span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleClear}
            style={{
              padding: '4px 8px',
              border: '1px solid #4a5568',
              background: 'transparent',
              color: '#a0aec0',
              cursor: 'pointer',
              fontSize: '11px',
              borderRadius: '4px',
            }}
          >
            Clear
          </button>
          <button
            onClick={handleClose}
            style={{
              padding: '4px 8px',
              border: '1px solid #4a5568',
              background: 'transparent',
              color: '#a0aec0',
              cursor: 'pointer',
              fontSize: '11px',
              borderRadius: '4px',
            }}
          >
            {isOpenViaButton ? 'Close' : 'Collapse'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid #2d3748',
          padding: '0 8px',
        }}
      >
        <TabButton
          id="raw"
          active={activeTab === 'raw'}
          onClick={setActiveTab}
          label="Raw"
        />
        <TabButton
          id="normalized"
          active={activeTab === 'normalized'}
          onClick={setActiveTab}
          label="Normalized"
        />
        <TabButton
          id="changes"
          active={activeTab === 'changes'}
          onClick={setActiveTab}
          label="Changes"
          badge={state.lastChanges.length}
        />
        <TabButton
          id="warnings"
          active={activeTab === 'warnings'}
          onClick={setActiveTab}
          label="Warnings"
          badge={state.warnings.length}
        />
        <TabButton
          id="tools"
          active={activeTab === 'tools'}
          onClick={setActiveTab}
          label="Tools"
        />
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {activeTab === 'raw' && (
          <JsonView
            data={state.rawInput}
            onCopy={handleCopy}
            copied={copied}
          />
        )}

        {activeTab === 'normalized' && (
          <JsonView
            data={state.normalizedInput}
            onCopy={handleCopy}
            copied={copied}
          />
        )}

        {activeTab === 'changes' && (
          <div style={{ height: '100%', overflow: 'auto' }}>
            {state.lastChanges.length === 0 ? (
              <div style={{ padding: '24px', color: '#718096', textAlign: 'center', fontSize: '12px' }}>
                No changes recorded yet.
                <br />
                Start answering questions to see live updates.
              </div>
            ) : (
              [...state.lastChanges].reverse().map((change, i) => (
                <ChangeItem key={`${change.ts}-${i}`} change={change} />
              ))
            )}
          </div>
        )}

        {activeTab === 'warnings' && (
          <div style={{ height: '100%', overflow: 'auto' }}>
            {state.warnings.length === 0 ? (
              <div style={{ padding: '24px', color: '#718096', textAlign: 'center', fontSize: '12px' }}>
                No warnings.
                <br />
                All mappings are valid.
              </div>
            ) : (
              state.warnings.map((warning, i) => (
                <WarningItem key={`${warning.ts}-${i}`} warning={warning} />
              ))
            )}
          </div>
        )}

        {activeTab === 'tools' && (
          <div style={{ height: '100%', overflow: 'auto', padding: '16px' }}>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ 
                color: '#f7fafc', 
                fontSize: '14px', 
                fontWeight: 600, 
                marginBottom: '12px' 
              }}>
                Open Document
              </h3>
              <p style={{ 
                color: '#a0aec0', 
                fontSize: '11px', 
                marginBottom: '12px',
                lineHeight: '1.5'
              }}>
                Load a saved report directly into the interactive survey by entering its document ID.
              </p>
              <input
                type="text"
                value={docIdInput}
                onChange={(e) => setDocIdInput(e.target.value)}
                placeholder="doc_abc123xyz"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleOpenDoc();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: '#2d3748',
                  border: '1px solid #4a5568',
                  borderRadius: '4px',
                  color: '#f7fafc',
                  fontSize: '12px',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  marginBottom: '8px'
                }}
              />
              <button
                onClick={handleOpenDoc}
                disabled={!docIdInput.trim()}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: docIdInput.trim() ? '#2563eb' : '#4a5568',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: docIdInput.trim() ? 'pointer' : 'not-allowed',
                  opacity: docIdInput.trim() ? 1 : 0.5
                }}
              >
                Open Document
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Footer */}
      <div
        style={{
          padding: '8px 16px',
          borderTop: '1px solid #2d3748',
          fontSize: '10px',
          color: '#718096',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>
          <a
            href={buildDevUrl()}
            style={{ color: '#718096', textDecoration: 'underline' }}
          >
            ?dev=1 to enable
          </a>
          {' | localStorage.devtools=1 persists'}
        </span>
        <span>{state.lastChanges.length} changes | {state.warnings.length} warnings</span>
      </div>
    </div>
  );
}

// =============================================================================
// DEVTOOLS INITIALIZATION
// =============================================================================

/**
 * Check if devtools should be enabled
 * RUNTIME ONLY - works on production builds
 */
export function shouldShowDevtools(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const safeStorageRead = (): boolean => {
    try {
      return localStorage.getItem('devtools') === '1';
    } catch {
      return false;
    }
  };

  const safeStorageWrite = (value: '1' | null): void => {
    try {
      if (value === null) localStorage.removeItem('devtools');
      else localStorage.setItem('devtools', value);
    } catch {
      // Ignore storage access failures (e.g. privacy modes)
    }
  };

  // Check for ?dev=0 to explicitly disable
  const urlParams = new URLSearchParams(window.location.search);
  const devParam = urlParams.get('dev');

  if (devParam === '0') {
    safeStorageWrite(null);
    if (typeof document !== 'undefined') {
      delete document.body.dataset.devtools;
    }
    return false;
  }

  // Check for ?dev=1 in URL
  const urlParamEnabled = devParam === '1';

  // Check localStorage (persists across sessions)
  const localFlag = safeStorageRead();

  // If URL param is present, persist to localStorage
  if (urlParamEnabled && !localFlag) {
    safeStorageWrite('1');
  }

  // Set indicator whenever devtools should be enabled
  const shouldEnable = urlParamEnabled || localFlag;
  if (shouldEnable && typeof document !== 'undefined') {
    document.body.dataset.devtools = 'enabled';
  }

  return shouldEnable;
}

/**
 * Initialize devtools
 * Call this at app startup
 */
export function initializeDevtools(): void {
  if (shouldShowDevtools()) {
    setInstrumentationEnabled(true);
    console.log('[DevTools] Mapping devtools enabled.');
  }
}

export default DevSidebar;
